import { ApplicationView, useController } from '@aha-app/mvc';
import React, { useEffect, useState } from 'react';
import WhiteboardController from '../../controllers/WhiteboardController';
import classNames from 'classnames';
import styles from '../../styles.module.css';
import ToolButton from '../../toolbars/ToolButton';
import ToolbarRow from '../../toolbars/ToolbarRow';
import ObjectMenuSection from '../../toolbars/ObjectMenu/ObjectMenuSection';
import {
  diagramVariants,
  diagramSetProperty,
  diagramSetVariant,
  diagramColorSchemes,
} from '@aha-app/diagrams';
import {
  diagramLanguageProvider,
  type MonacoEditor,
} from './DiagramLanguageProvider';
import { svgToIcon } from '../../utils/svg';
import { isSmartObject } from '../../objects/guards';
import useSelectedObject from '../hooks/useSelectedObject';
import type SmartObject from '../../objects/SmartObject';
import colorSwatchSvg from '../../assets/icons/colorSwatch.svg';
import ObjectTextArea from './ObjectTextArea';

interface VariantPopupProps {
  selectedObject: SmartObject;
}

const VariantPopup: React.FC<VariantPopupProps> = ApplicationView(
  ({ selectedObject }) => {
    const controller = useController(WhiteboardController);
    const variantSections = diagramVariants();

    const applyVariant = (diagramType: string, value: string) => () => {
      const newText = diagramSetVariant(
        selectedObject.textContent,
        diagramType,
        value
      );

      selectedObject.set('textContent', newText);
      controller.dispatchEvent('smartObject.updated', {
        smartObjectId: selectedObject.id,
      });
    };

    return (
      <div className={classNames(styles.objectMenuContent, styles.toolbarMenu)}>
        {variantSections.map(section => (
          <ObjectMenuSection
            key={section.diagramName}
            title={section.diagramName}
          >
            {section.variants.map(variant => (
              <ToolButton
                key={variant.value}
                className={styles.labeledToolButton}
                onClick={applyVariant(section.diagramType, variant.value)}
              >
                <div className={styles.labeledToolButtonContent}>
                  <span className={styles.labeledToolButtonIcon}>
                    {svgToIcon(variant.icon)}
                  </span>
                </div>
              </ToolButton>
            ))}
          </ObjectMenuSection>
        ))}
      </div>
    );
  }
);

interface ColorSchemePopupProps {
  selectedObject: SmartObject;
}

const ColorSchemePopup: React.FC<ColorSchemePopupProps> = ApplicationView(
  ({ selectedObject }) => {
    const controller = useController(WhiteboardController);
    const colorSchemes = diagramColorSchemes();

    const applyColorScheme = (value: string) => () => {
      const newText = diagramSetProperty(
        selectedObject.textContent,
        'colorScheme',
        value
      );

      selectedObject.set('textContent', newText);
      controller.dispatchEvent('smartObject.updated', {
        smartObjectId: selectedObject.id,
      });
    };

    return (
      <div className={classNames(styles.objectMenuContent, styles.toolbarMenu)}>
        {colorSchemes.map(colorScheme => (
          <ToolButton
            key={colorScheme.value}
            className={styles.labeledToolButton}
            onClick={applyColorScheme(colorScheme.value)}
          >
            <div className={styles.labeledToolButtonContent}>
              <span className={styles.labeledToolButtonIcon}>
                {svgToIcon(colorScheme.icon)}
              </span>
            </div>
          </ToolButton>
        ))}
      </div>
    );
  }
);

const SmartObjectEditor: React.FC = () => {
  const controller = useController(WhiteboardController);

  const selectedObject = useSelectedObject({
    predicate: isSmartObject,
  });

  useEffect(() => {
    return () => {
      // complete any open undo group when unmounting or changing selection
      // if there were no changes, this will be a no-op
      controller.undoManager.completeUndoGroup();
    };
  }, [controller, selectedObject]);

  // We need to load the monaco-editor asynchronously
  const [monaco, setMonaco] = useState<MonacoEditor>();
  useEffect(() => {
    if (monaco) {
      return;
    }

    const loadMonaco = async () => {
      const monaco = await diagramLanguageProvider();
      setMonaco(monaco);
    };

    loadMonaco();
  }, []);

  if (!selectedObject) {
    return null;
  }

  return (
    <div style={{ height: '100%' }}>
      <ToolbarRow>
        <ToolButton
          className={styles.stringToolButton}
          popup={
            <VariantPopup
              key={selectedObject.id}
              selectedObject={selectedObject}
            />
          }
        >
          Layout &nbsp;
          <i className='fas fa-angle-down' />
        </ToolButton>

        <ToolButton
          popup={
            <ColorSchemePopup
              key={selectedObject.id}
              selectedObject={selectedObject}
            />
          }
        >
          {svgToIcon(colorSwatchSvg)}
        </ToolButton>
      </ToolbarRow>

      {monaco && (
        <ObjectTextArea
          key={selectedObject.id}
          monaco={monaco}
          selectedObject={selectedObject}
        />
      )}
    </div>
  );
};

export default ApplicationView(SmartObjectEditor);
